import ApplicantFilter from "@Components/Filters/ApplicantFilter";
import TextFilter from "@Components/Filters/TextFilter";
import IconButton from "@Components/IconButton";
import Loading from "@Components/Loading";
import Select from "@Components/Select";
import Table from "@Components/Table";
import { Column } from "@Components/Table/types";
import { useGetTopDealers } from "@Hooks/useDealers";
import {
  DealerDashboardData,
  useGetDalersDashboard,
} from "@Hooks/useDealersDashboard";
import useGetDeals from "@Hooks/useDeals";
import {
  formatCollateral,
  formatCustomersNames,
} from "@Pages/Applications/Applications";
import CountySelect from "@Pages/Dealers/DealerMap/Filters/CountySelect";
import MonthYearSelect from "@Pages/Dealers/DealerMap/Filters/MonthYearSelect";
import StateSelect from "@Pages/Dealers/DealerMap/Filters/StateSelect";
import { Deal } from "@Types/deal";
import { Dealership } from "@Types/dealerships";
import { formatDate, formatPrice } from "@Utils/functions";
import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { AiOutlineArrowRight } from "react-icons/ai";
import { FaCar, FaChartLine } from "react-icons/fa6";
import { IoDocumentTextOutline } from "react-icons/io5";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./DealersDashboard.module.scss";
import LenderDecisionsByStatusSection from "./LenderDecisionsByStatusSection";

interface DashboardProps {
  dashboardSelect: JSX.Element | null;
}
export const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
export interface DealerDashboardFilters {
  county?: string;
  state?: string;
  year?: number | undefined | null;
  month?: number | undefined | null;
  filterType?: "all" | "monthly" | "yearly";
  basedOnLocationOf?: "dealer" | "applicant" | undefined;
}

const getPeriodLabel = (filters: DealerDashboardFilters) => {
  if (filters.filterType === "all" || !filters.year) return "All time";
  if (filters.filterType === "yearly" || !filters.month)
    return `${filters.year}`;
  return `${monthNames[filters.month - 1]} ${filters.year}`;
};

const getInitials = (name: string | undefined | null) => {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const DateModal = ({
  setFilters,
  filters,
  setCurrentModal,
}: {
  setFilters: React.Dispatch<React.SetStateAction<DealerDashboardFilters>>;
  filters: DealerDashboardFilters;
  setCurrentModal: React.Dispatch<
    React.SetStateAction<"location" | "date" | null>
  >;
}) => {
  return (
    <div className={styles.filtersButtonModal}>
      <div>
        <Select
          placeholder="Year"
          name="Year"
          id="year"
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, year: parseInt(e.target.value) }))
          }
          value={filters.year || ""}
          options={[
            {
              label: "All",
              value: "",
            },
            ...Array.from({ length: 10 }, (_, i) => ({
              label: `${new Date().getFullYear() - i}`,
              value: `${new Date().getFullYear() - i}`,
            })),
          ]}
        />
      </div>
      <div>
        <Select
          placeholder="Month"
          name="Month"
          id="Month"
          disabled={!filters.year}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              month: parseInt(e.target.value),
            }))
          }
          value={filters.month || ""}
          options={[
            {
              label: "All",
              value: "",
            },
            ...Array.from({ length: 12 }, (_, i) => ({
              label: monthNames[i],
              value: `${i + 1}`,
            })),
          ]}
        />
      </div>

      <button
        className={styles.filtersButtonModalClose}
        onClick={(e) => {
          e.stopPropagation();
          setCurrentModal(null);
        }}
      >
        Close
      </button>
    </div>
  );
};

const KpiTilesSection = ({
  data,
}: {
  data: DealerDashboardData | undefined;
}) => {
  const d = data?.data;
  return (
    <div className={styles.statGroup}>
      <div className={styles.kpiGrid}>
        <div className={styles.kpiTile}>
          <div className={styles.kpiEyebrow}>Amount financed</div>
          <div className={styles.kpiValue}>
            {formatPrice(d?.amountFinanced || 0)}
          </div>
          <div className={styles.kpiSupport}>
            Across {d?.totalFundedDeals || 0} funded loans
          </div>
        </div>
        <div className={styles.kpiTile}>
          <div className={styles.kpiEyebrow}>Funded loans</div>
          <div className={styles.kpiValue}>{d?.totalFundedDeals || 0}</div>
          <div className={styles.kpiSupport}>
            {(d?.averageInterestRate || 0).toFixed(2)}% avg. interest rate
          </div>
        </div>
        <div className={styles.kpiTile}>
          <div className={styles.kpiEyebrow}>Total applications</div>
          <div className={styles.kpiValue}>
            {parseInt((d?.totalApplications || 0).toString())}
          </div>
          <div className={styles.kpiSupport}>
            {formatPrice(d?.totalAmountRequested || 0)} requested
          </div>
        </div>
        <div className={`${styles.kpiTile} ${styles.kpiTileAccent}`}>
          <div className={styles.kpiEyebrow}>Look to book</div>
          <div className={styles.kpiValue}>
            {Math.round(d?.lookToBook || 0)}%
          </div>
          <div className={styles.kpiSupport}>
            Based on {d?.allDealForLTB || 0} finished applications
          </div>
        </div>
      </div>
    </div>
  );
};

const ApplicantProfileStrip = ({
  data,
}: {
  data: DealerDashboardData | undefined;
}) => {
  const d = data?.data;
  return (
    <div className={styles.statCard}>
      <div className={styles.statGroupTitle}>Applicant profile</div>
      <div className={styles.statStrip}>
        <div className={styles.statCell}>
          <div className={styles.statLabel}>Credit score</div>
          <div className={styles.statValueSplit}>
            <div className={styles.statValueHalf}>
              <span className={styles.statValueHalfNumber}>
                {parseInt(d?.averageApplicantCreditScore?.toString() || "0")}
              </span>
              <span className={styles.statValueHalfLabel}>Applicant</span>
            </div>
            <div className={styles.statValueHalf}>
              <span className={styles.statValueHalfNumber}>
                {parseInt(d?.averageCoApplicantCreditScore?.toString() || "0")}
              </span>
              <span className={styles.statValueHalfLabel}>Co-applicant</span>
            </div>
          </div>
        </div>
        <div className={styles.statCell}>
          <div className={styles.statLabel}>Average age</div>
          <div className={styles.statValue}>
            {parseInt(d?.averageAge?.toString() || "0")}
          </div>
        </div>
        <div className={styles.statCell}>
          <div className={styles.statLabel}>Average DTI</div>
          <div className={`${styles.statValue} ${styles.statValueMuted}`}>
            N/A
          </div>
        </div>
      </div>
    </div>
  );
};

const LoanTermsStrip = ({
  data,
}: {
  data: DealerDashboardData | undefined;
}) => {
  const d = data?.data;
  return (
    <div className={styles.statCard}>
      <div className={styles.statGroupTitle}>Loan terms</div>
      <div className={styles.statStrip}>
        <div className={styles.statCell}>
          <div className={styles.statLabel}>Interest rate</div>
          <div className={styles.statValue}>
            {(d?.averageInterestRate || 0).toFixed(2)}%
          </div>
        </div>
        <div className={styles.statCell}>
          <div className={styles.statLabel}>LTV</div>
          <div className={styles.statValue}>
            {(d?.averageLoanToValue || 0).toFixed(2)}%
          </div>
        </div>
        <div className={styles.statCell}>
          <div className={styles.statLabel}>Term (median)</div>
          <div className={styles.statValue}>{d?.medianTerm || 0} mo</div>
        </div>
        <div className={styles.statCell}>
          <div className={styles.statLabel}>Amount requested</div>
          <div className={styles.statValue}>
            {formatPrice(d?.totalAmountRequested || 0)}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatCompactPrice = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);

const FundedAmountChartSection = ({
  data,
  filters,
}: {
  data: DealerDashboardData | undefined;
  filters: DealerDashboardFilters;
}) => {
  const series = [
    {
      name: "Amount financed",
      data: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
        (month) =>
          data?.data?.amountFinancedByMonth.find((stat) => stat.month === month)
            ?.totalAmountFinanced || 0,
      ),
    },
  ];
  const options: ApexCharts.ApexOptions = {
    chart: {
      height: 260,
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: "55%",
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return formatCompactPrice(val);
      },
      offsetY: -18,
      style: {
        fontSize: "10px",
        fontWeight: 600,
        colors: ["#5e5d65"],
      },
    },
    colors: ["#6b97f6"],
    tooltip: {
      enabled: true,
      y: {
        formatter: function (val: number) {
          return formatPrice(val) || "";
        },
      },
    },
    xaxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: "#9e9ca7",
          fontSize: "11px",
          fontWeight: 600,
          fontFamily: "'IBM Plex Mono', 'Roboto Condensed'",
        },
      },
    },
    yaxis: {
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: false,
      },
    },
    grid: {
      borderColor: "#e8e8ec",
      strokeDashArray: 3,
    },
  };
  return (
    <div className={styles.sectionContainer}>
      <div className={styles.sectionTitle}>
        <span className={styles.sectionTitleLabel}>
          <FaChartLine size={16} />
          Performance
        </span>
      </div>

      <div className={styles.chartSubtitle}>
        Funded amount financed by month (
        {filters.year || new Date().getFullYear()})
      </div>

      <ReactApexChart
        options={options}
        series={series}
        type="bar"
        height={260}
      />
    </div>
  );
};

const TopDealersSection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 1;
  const columns: Column<Dealership>[] = [
    {
      id: "name",
      label: "Name",
      value: (dealer) => {
        const name = dealer?.data?.info?.displayName || "N/A";
        return (
          <div className={styles.avatarNameCell}>
            <span className={styles.avatarSquare}>{getInitials(name)}</span>
            <span>{name}</span>
          </div>
        );
      },
    },
    {
      id: "loansCount",
      label: "Loans Count",
      value: (dealer) => dealer?.dealCount?.toString() || "0",
      style: () => ({ justifyContent: "flex-end" }),
    },
    {
      id: "amountFinanced",
      label: "Amount Financed",
      value: (dealer) => formatPrice(dealer?.totalAmountFinanced ?? 0) ?? "0",
      style: () => ({ justifyContent: "flex-end" }),
    },
    {
      id: "state",
      label: "State",
      value: (dealer) => dealer.data?.info?.state || "N/A",
    },
    {
      id: "latestDealDate",
      label: "Latest Deal Date",
      value: (dealer) =>
        dealer?.latestFundedAt
          ? formatDate(new Date(dealer?.latestFundedAt), "medium")
          : "N/A",
    },
    {
      id: "firstDealDate",
      label: "First Deal Date",
      value: (dealer) =>
        dealer?.firstFundedAt
          ? formatDate(new Date(dealer?.firstFundedAt), "medium")
          : "N/A",
    },
    {
      id: "actions",
      label: "Actions",
      value: (deal) => (
        <IconButton
          id={`open-dealer-${deal?._id}`}
          outerClassName={styles.rowActionButton}
          onClick={() => {
            navigate(`/dealers/${deal._id}?page=${currentPage}`);
          }}
        >
          <AiOutlineArrowRight size={16} />
        </IconButton>
      ),
    },
  ];

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.sectionTitle}>
        <span className={styles.sectionTitleLabel}>
          <FaCar size={16} />
          Top dealers
          <span className={styles.sectionTitleMuted}>by amount financed</span>
        </span>
        <button className={styles.seeAllButton}>
          See all <AiOutlineArrowRight size={13} />
        </button>
      </div>

      <div className={styles.premiumTableWrapper}>
        <Table<Dealership>
          outerItemsPerPage={5}
          columns={columns}
          useGetFunction={useGetTopDealers}
          entityType="dealers"
        />
      </div>
    </div>
  );
};

const LatestLoansSection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 1;
  const columns: Column<Deal>[] = [
    {
      label: "Customer",
      value: (deal) => {
        const name = formatCustomersNames(deal);
        return (
          <div className={styles.avatarNameCell}>
            <span className={styles.avatarSquare}>{getInitials(name)}</span>
            <span>{name}</span>
          </div>
        );
      },
      id: "customer",
      truncate: 15,
      filters: [
        {
          placeholder: "Customer",
          preview: ApplicantFilter,
          queryPath: [],
        },
      ],
    },
    {
      id: "collateral",
      label: "Collateral",
      truncate: 15,
      value: (deal) => formatCollateral(deal),
      sortPath: ["data", "info", "vehicle", "make"],
      filters: [
        {
          placeholder: "Collateral",
          preview: TextFilter,
          queryPath: ["data", "info", "vehicle", "make"],
          partialSearch: true,
          caseInsensitive: true,
        },
      ],
    },
    {
      id: "dealer",
      label: "Dealership",
      truncate: 15,
      value: (deal) => deal?.data?.dealership?.data?.info?.name,
    },
    {
      label: "State",
      value: (deal) => deal?.data?.applicant?.data?.info?.currentState ?? "",
      id: "state",
      truncate: 15,
      filters: [
        {
          placeholder: "State",
          preview: TextFilter,
          queryPath: ["data", "applicant", "data", "info", "currentState"],
        },
      ],
    },

    {
      id: "actions",
      label: "Actions",
      value: (deal) => (
        <IconButton
          id={`open-deal-${deal?._id}`}
          outerClassName={styles.rowActionButton}
          onClick={() => {
            navigate(`/loans/${deal._id}?page=${currentPage}`);
          }}
        >
          <AiOutlineArrowRight size={16} />
        </IconButton>
      ),
    },
  ];
  return (
    <div className={styles.sectionContainer}>
      <div className={styles.sectionTitle}>
        <span className={styles.sectionTitleLabel}>
          <IoDocumentTextOutline size={16} />
          Latest loans
        </span>
        <button className={styles.seeAllButton}>
          See all <AiOutlineArrowRight size={13} />
        </button>
      </div>

      <div className={styles.premiumTableWrapper}>
        <Table<Deal>
          id="latest-loans-table"
          outerItemsPerPage={5}
          columns={columns}
          useGetFunction={useGetDeals}
          entityType="loans"
        />
      </div>
    </div>
  );
};

const DealersDashboard = ({ dashboardSelect }: DashboardProps) => {
  const [filters, setFilters] = useState<DealerDashboardFilters>({
    county: "",
    state: "",
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    filterType: "all",
    basedOnLocationOf: "applicant",
  });

  const { data, isLoading } = useGetDalersDashboard(filters);

  const handleStateSelect = (
    state: string | null,
    basedOnLocationOf: "applicant" | "dealer",
  ) => {
    setFilters((prev) => ({
      ...prev,
      basedOnLocationOf: basedOnLocationOf,
      state: state || "",
      county: "",
    }));
  };
  const handleCountySelect = (county: string | null) => {
    setFilters((prev) => ({
      ...prev,
      county: county || "",
    }));
  };

  const setFilterType = (type: "all" | "monthly" | "yearly") => {
    setFilters((prev) => ({
      ...prev,
      filterType: type,
    }));
  };
  const handleDateSelect = (month: number | undefined | null, year: number) => {
    setFilters((prev) => ({
      ...prev,
      month,
      year,
    }));
  };
  const handleClearAllFilters = () => {
    setFilters({
      county: "",
      state: "",
      year: undefined,
      month: undefined,
      filterType: "all",
      basedOnLocationOf: "applicant",
    });
  };

  const Filters = () => {
    return (
      <>
        <div className={styles.pageHeader}>
          <div>
            <div className={styles.pageEyebrow}>{getPeriodLabel(filters)}</div>
            <div className={styles.pageTitle}>Dealers Dashboard</div>
          </div>
          {dashboardSelect && (
            <div className={styles.pageHeaderActions}>{dashboardSelect}</div>
          )}
        </div>

        <div className={styles.filterBar}>
          <div className={styles.filterBarControls}>
            <div className={styles.periodSegmented}>
              <button
                className={`${styles.periodSegmentedOption} ${
                  filters.filterType === "monthly"
                    ? styles.periodSegmentedOptionActive
                    : ""
                }`}
                onClick={() => setFilterType("monthly")}
              >
                Monthly
              </button>
              <button
                className={`${styles.periodSegmentedOption} ${
                  filters.filterType === "yearly"
                    ? styles.periodSegmentedOptionActive
                    : ""
                }`}
                onClick={() => setFilterType("yearly")}
              >
                Yearly
              </button>
              <button
                className={`${styles.periodSegmentedOption} ${
                  filters.filterType === "all"
                    ? styles.periodSegmentedOptionActive
                    : ""
                }`}
                onClick={() => setFilterType("all")}
              >
                All time
              </button>
            </div>

            <div className={styles.filterSlot}>
              <label className={styles.filterLabel}>State</label>
              <StateSelect
                className={styles.filterSlotControl}
                allAvailableStates={data?.data?.allAvailableStates || []}
                stateId={filters.state}
                onSelect={handleStateSelect}
                defaultBasedOnLocationOf={filters.basedOnLocationOf || "dealer"}
                defaultState={filters.state}
              />
            </div>

            {filters.state && (
              <div className={styles.filterSlot}>
                <label className={styles.filterLabel}>County</label>
                <CountySelect
                  className={styles.filterSlotControl}
                  allAvailableCounties={data?.data?.allAvailableCounties || []}
                  county={filters.county}
                  state={filters.state}
                  onSelect={handleCountySelect}
                />
              </div>
            )}

            <div className={styles.filterSlot}>
              <label className={styles.filterLabel}>Date</label>
              <MonthYearSelect
                className={styles.filterSlotControl}
                onSelect={handleDateSelect}
                mode={filters.filterType}
                month={filters.month}
                year={filters.year}
              />
            </div>
          </div>

          <button
            className={styles.clearAllLink}
            onClick={handleClearAllFilters}
          >
            Clear all
          </button>
        </div>
      </>
    );
  };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.dashboardCard}>
        <Filters />

        {isLoading && (
          <div className={styles.loadingWrap}>
            <Loading text={"Loading"} />
          </div>
        )}
        {!isLoading && !data && (
          <div className={styles.emptyState}>No data found</div>
        )}
        {!isLoading && data && (
          <>
            <KpiTilesSection data={data} />
            <div className={styles.applicantLoanRow}>
              <ApplicantProfileStrip data={data} />
              <LoanTermsStrip data={data} />
            </div>

            <div className={styles.decisionsChartRow}>
              <LenderDecisionsByStatusSection filters={filters} />
              <FundedAmountChartSection data={data} filters={filters} />
            </div>

            <TopDealersSection />
            <LatestLoansSection />
          </>
        )}
      </div>
    </div>
  );
};

export default DealersDashboard;
